import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  MoreHorizontal,
  FileText,
  BookOpen,
  Video,
  Image,
  ExternalLink,
  Calendar,
} from 'lucide-react';
import { Card } from '../../components/Card';
import { Badge, getStatusVariant } from '../../components/Badge';
import { Button } from '../../components/Button';
import { contentApi } from '../../lib/api';
import { format } from 'date-fns';
import type { Content, ContentType, ContentStatus, ContentPillar } from '../../types';

const TYPE_OPTIONS: ContentType[] = ['blog_post', 'case_study', 'lead_magnet', 'video', 'infographic', 'whitepaper'];
const STATUS_OPTIONS: ContentStatus[] = ['idea', 'draft', 'review', 'scheduled', 'published', 'archived'];
const PILLAR_OPTIONS: ContentPillar[] = ['daily_operations', 'cash_flow', 'growth', 'technology', 'leadership'];

function getContentIcon(type: ContentType) {
  switch (type) {
    case 'blog_post':
      return <FileText className="w-5 h-5" />;
    case 'case_study':
      return <BookOpen className="w-5 h-5" />;
    case 'video':
      return <Video className="w-5 h-5" />;
    case 'infographic':
      return <Image className="w-5 h-5" />;
    default:
      return <FileText className="w-5 h-5" />;
  }
}

export function ContentLibrary() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pillarFilter, setPillarFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['content', typeFilter, statusFilter, pillarFilter],
    queryFn: () =>
      contentApi.list({
        content_type: typeFilter || undefined,
        status: statusFilter || undefined,
        pillar: pillarFilter || undefined,
        limit: 100,
      }),
  });

  const updateContentMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ContentStatus }) =>
      contentApi.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });

  const contentList = (data?.content || []) as Content[];

  // Filter by search query
  const filteredContent = contentList.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.title.toLowerCase().includes(query) ||
      item.excerpt?.toLowerCase().includes(query)
    );
  });

  // Calculate stats
  const stats = {
    total: contentList.length,
    published: contentList.filter((c) => c.status === 'published').length,
    draft: contentList.filter((c) => c.status === 'draft').length,
    scheduled: contentList.filter((c) => c.status === 'scheduled').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Library</h1>
          <p className="text-gray-500 mt-1">Manage your content assets</p>
        </div>
        <Button>
          <Plus className="w-4 h-4" />
          New Content
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">Total Content</p>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">Published</p>
            <p className="text-2xl font-semibold text-green-600">{stats.published}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">Drafts</p>
            <p className="text-2xl font-semibold text-yellow-600">{stats.draft}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">Scheduled</p>
            <p className="text-2xl font-semibold text-blue-600">{stats.scheduled}</p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Types</option>
          {TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>
              {type.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <select
          value={pillarFilter}
          onChange={(e) => setPillarFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Pillars</option>
          {PILLAR_OPTIONS.map((pillar) => (
            <option key={pillar} value={pillar}>
              {pillar.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </option>
          ))}
        </select>
      </div>

      {/* Content Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : filteredContent.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No content found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContent.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow overflow-hidden">
              {/* Featured Image */}
              {item.featured_image ? (
                <div className="h-40 overflow-hidden">
                  <img
                    src={item.featured_image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-40 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center">
                  {getContentIcon(item.content_type)}
                </div>
              )}

              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 line-clamp-2">{item.title}</h3>
                  <button className="p-1 hover:bg-gray-100 rounded flex-shrink-0">
                    <MoreHorizontal className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                {item.excerpt && (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">{item.excerpt}</p>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant={getStatusVariant(item.status)}>{item.status}</Badge>
                  <Badge variant="default">
                    {item.content_type.replace('_', ' ')}
                  </Badge>
                  {item.pillar && (
                    <Badge variant="purple">{item.pillar.replace('_', ' ')}</Badge>
                  )}
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {item.published_at
                        ? format(new Date(item.published_at), 'MMM d, yyyy')
                        : item.scheduled_for
                        ? `Scheduled: ${format(new Date(item.scheduled_for), 'MMM d')}`
                        : format(new Date(item.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>

                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>

                {/* Quick status change */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <select
                    value={item.status}
                    onChange={(e) =>
                      updateContentMutation.mutate({
                        id: item.id,
                        status: e.target.value as ContentStatus,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
